import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/store/authStore';
import type { LoginCredentials, SignupCredentials } from '@shared/types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, setUser, logout: clearUser } = useAuthStore();
  const [emailConfirmationSent, setEmailConfirmationSent] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: (user) => {
      setUser(user);
      // Navigation is handled by LoginPage's redirect effect, which reads the
      // cached company's product_mode to decide whether to land on /feedback or
      // /dashboard. Do NOT navigate here to avoid a double-navigate race.
    },
  });

  const signupMutation = useMutation({
    mutationFn: (credentials: SignupCredentials) => authService.signup(credentials),
    onSuccess: (result) => {
      if ('emailConfirmationRequired' in result) {
        // Email confirmation required - show message
        setEmailConfirmationSent(result.email);
      } else {
        // Direct login (email confirmation disabled)
        setUser(result);
        navigate('/dashboard');
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      clearUser();
      // Drop the entire React Query in-memory cache so stale company data
      // (including productMode) cannot survive an account switch and drive
      // the post-login redirect into the wrong product shell (D-16c).
      queryClient.clear();
      navigate('/login');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => authService.resetPassword(email),
  });

  const updatePasswordMutation = useMutation({
    mutationFn: (newPassword: string) => authService.updatePassword(newPassword),
    onSuccess: () => {
      navigate('/login');
    },
  });

  const sendMagicLinkMutation = useMutation({
    mutationFn: (email: string) => authService.sendMagicLink(email),
  });

  const signInWithGoogleMutation = useMutation({
    mutationFn: () => authService.signInWithGoogle(),
  });

  return {
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    signup: signupMutation.mutate,
    logout: logoutMutation.mutate,
    resetPassword: resetPasswordMutation.mutate,
    updatePassword: updatePasswordMutation.mutate,
    sendMagicLink: sendMagicLinkMutation.mutate,
    isLoginLoading: loginMutation.isPending,
    isSignupLoading: signupMutation.isPending,
    isResetPasswordLoading: resetPasswordMutation.isPending,
    isUpdatePasswordLoading: updatePasswordMutation.isPending,
    isSendMagicLinkLoading: sendMagicLinkMutation.isPending,
    loginError: loginMutation.error,
    signupError: signupMutation.error,
    resetPasswordError: resetPasswordMutation.error,
    resetPasswordSuccess: resetPasswordMutation.isSuccess,
    updatePasswordError: updatePasswordMutation.error,
    updatePasswordSuccess: updatePasswordMutation.isSuccess,
    sendMagicLinkError: sendMagicLinkMutation.error,
    sendMagicLinkSuccess: sendMagicLinkMutation.isSuccess,
    signInWithGoogle: signInWithGoogleMutation.mutate,
    isSignInWithGoogleLoading: signInWithGoogleMutation.isPending,
    signInWithGoogleError: signInWithGoogleMutation.error,
    emailConfirmationSent,
  };
}
